import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FetchInsurancePrompt } from '@/components/PriceCalculator/CurrentInsuranceField/FetchInsurancePrompt/FetchInsurancePrompt'
import { useFetchInsurancePrompt } from '@/components/PriceCalculator/CurrentInsuranceField/FetchInsurancePrompt/useFetchInsurancePrompt'
import { FetchedInsurancesListDialog } from '@/features/insurely/FetchedInsurancesListDialog'
import { usePriceIntentValueOrThrow } from '@/features/priceCalculator/usePriceIntent'
import {
  type ExternalInsurerFragment,
  useInsurelyDataCollectionCreateMutation,
  usePriceIntentInsurelyUpdateMutation,
} from '@/services/graphql/generated'
import { InsurelyIframe, type InsurelyIframeProps } from '@/services/Insurely/InsurelyIframe'
import { logger } from '@/services/logger/logger.client'
import { Dialog } from 'ui/src/components/Dialog/Dialog'
import { CrossIcon } from 'ui/src/icons/CrossIcon'
import { visuallyHidden } from 'ui/src/utils/visibility.css'
import { useFetchInsurance } from '../useFetchInsurance'
import { dialogIframeCloseBtn, dialogIframeWindow } from './FetchInsurance.css'

const datadogLogger = logger.createLogger({ name: 'FetchInsurance' })

type OnCompleted = NonNullable<InsurelyIframeProps['onCompleted']>

type FetchInsuranceProps = InsurelyDialogProps & { companyName: string }

export function FetchInsurance({ companyName, ...props }: FetchInsuranceProps) {
  const { confirm, skip } = useFetchInsurancePrompt()
  const { compare } = useFetchInsurance()
  const priceIntent = usePriceIntentValueOrThrow()
  const fetchedInsurances = priceIntent.fetchedExternalInsurances ?? []

  return (
    <>
      <FetchInsurancePrompt
        onConfirm={compare}
        onSkip={skip}
        company={companyName}
        insurelyId={props.externalInsurer.insurelyId}
      />
      <InsurelyDialog {...props} />
      <FetchedInsurancesListDialog
        insurances={fetchedInsurances}
        externalInsurerName={props.externalInsurer.displayName}
        onConfirm={confirm}
      />
    </>
  )
}

type InsurelyDialogProps = {
  priceIntentId: string
  externalInsurer: ExternalInsurerFragment
  insurely: { configName: string; partner: string }
  productName: string
}

function InsurelyDialog({ priceIntentId, insurely, externalInsurer, productName }: InsurelyDialogProps) {
  const loggingContext = useMemo(
    () => ({
      priceIntentId: priceIntentId,
      companyName: externalInsurer.displayName,
      productName: productName,
    }),
    [priceIntentId, externalInsurer.displayName, productName],
  )

  const { t } = useTranslation('purchase-form')
  const { state, dismiss, success } = useFetchInsurance()

  const [dataCollectionId, setDataCollectionId] = useState<string | null>(null)
  const [updateDataCollectionId] = usePriceIntentInsurelyUpdateMutation({
    onCompleted({ priceIntentInsurelyUpdate }) {
      datadogLogger.info('Updated Insurely data collection ID', loggingContext)

      const updatedPriceIntent = priceIntentInsurelyUpdate.priceIntent
      if (!updatedPriceIntent?.externalInsurer) {
        datadogLogger.warn('Failed to update Insurely data collection ID', loggingContext)
        dismiss()
      }
    },
    onError(error) {
      datadogLogger.warn('Error updating Insurely data collection ID', {
        ...loggingContext,
        error,
      })
    },
  })

  const [createDataCollection] = useInsurelyDataCollectionCreateMutation({
    onCompleted(data) {
      const { dataCollectionId } = data.insurelyInitiateIframeDataCollection
      setDataCollectionId(dataCollectionId)
      datadogLogger.setContextProperty('dataCollectionId', dataCollectionId)
      datadogLogger.info(`Created data collection ID: ${dataCollectionId}`)
    },
    onError(error) {
      datadogLogger.warn('Error creating Insurely data collection', {
        ...loggingContext,
        error,
      })
    },
  })

  const handleInsurelyCollection = (collectionId: string) => {
    createDataCollection({ variables: { collectionId, partner: insurely.partner } })
  }

  const handleInsurelyCompleted: OnCompleted = useCallback(async () => {
    if (dataCollectionId) {
      datadogLogger.info(`Saving Insurely data collection ID: ${dataCollectionId}, priceIntentId: ${priceIntentId}`)
      await updateDataCollectionId({
        variables: { priceIntentId, dataCollectionId },
      })
    } else {
      datadogLogger.error('Completed Insurely without creating data collection ID', loggingContext)
    }

    success()
  }, [updateDataCollectionId, priceIntentId, dataCollectionId, loggingContext, success])

  return (
    <Dialog.Root open={state === 'COMPARE'} onOpenChange={dismiss}>
      <Dialog.Content onClose={dismiss} centerContent={true} frostedOverlay={true} asChild={true}>
        <Dialog.Window className={dialogIframeWindow}>
          <Dialog.Close className={dialogIframeCloseBtn}>
            <CrossIcon size="1.5rem" />
          </Dialog.Close>

          <Dialog.Title className={visuallyHidden}>{t('FETCH_INSURANCE_PROMPT_TITLE')}</Dialog.Title>
          <InsurelyIframe
            configName={insurely.configName}
            onCollection={handleInsurelyCollection}
            onCompleted={handleInsurelyCompleted}
          />
        </Dialog.Window>
      </Dialog.Content>
    </Dialog.Root>
  )
}
